import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { selectSportsClubId, selectUserId, addTeam, removeTeam } from '../store/userSlice';
import { useAuth } from '../context/AuthContext';
import { TeamControllerService, DictionaryControllerService, UserManagementControllerService, SportsClubControllerService, UserTeamControllerService } from '../api/index.ts';
import { toast } from 'react-toastify';
import './TeamManagement.css';

function TeamManagement({ embedded }) {
  const dispatch = useDispatch();
  const { hasRole } = useAuth();
  const isCoach = hasRole('COACH');
  const isSportsClub = hasRole('SPORTS_CLUB');
  const sportsClubId = useSelector(selectSportsClubId);
  const userId = useSelector(selectUserId);

  const [teams, setTeams] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [mobileCoachTab, setMobileCoachTab] = useState('approved'); // 'approved' lub 'pending'
  const [dictionaries, setDictionaries] = useState({});

  // Modal dla dodawania drużyn (tylko SPORTS_CLUB)
  const [showSeniorTeamModal, setShowSeniorTeamModal] = useState(false);
  const [showYouthTeamModal, setShowYouthTeamModal] = useState(false);
  const [newSeniorTeam, setNewSeniorTeam] = useState({
    teamType: 'SENIOR',
    seniorTier: '',
    seniorTeamType: '',
    gender: '',
    teamMode: 'SEASONAL'
  });
  const [newYouthTeam, setNewYouthTeam] = useState({
    teamType: 'YOUTH',
    youthTeamType: '',
    yearOfBirth: '',
    youthTier: '',
    gender: '',
    teamMode: 'SEASONAL'
  });
  const [isSaving, setIsSaving] = useState(false);

  // Modal potwierdzenia usunięcia (SPORTS_CLUB)
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [teamToDelete, setTeamToDelete] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);

  // Modal edycji poziomu (SPORTS_CLUB)
  const [showEditTierModal, setShowEditTierModal] = useState(false);
  const [teamToEditTier, setTeamToEditTier] = useState(null);
  const [selectedTier, setSelectedTier] = useState('');
  const [isUpdatingTier, setIsUpdatingTier] = useState(false);

  // Modal potwierdzenia usunięcia przypisania (COACH)
  const [showUnassignConfirm, setShowUnassignConfirm] = useState(false);
  const [teamToUnassign, setTeamToUnassign] = useState(null);
  const [isUnassigning, setIsUnassigning] = useState(false);

  // Coach: modal dodawania drużyny (wizard)
  const [showCoachAddTeamModal, setShowCoachAddTeamModal] = useState(false);
  const [coachWizardStep, setCoachWizardStep] = useState(1);
  const [wizardTempSelectedTeamIds, setWizardTempSelectedTeamIds] = useState([]);
  const [wizardSelectedClubId, setWizardSelectedClubId] = useState(null);
  const [wizardClubTeams, setWizardClubTeams] = useState([]);
  const [isLoadingWizardTeams, setIsLoadingWizardTeams] = useState(false);
  const [isAddingPending, setIsAddingPending] = useState(false);

  // Coach: wyszukiwarka klubów
  const [territories, setTerritories] = useState([]);
  const [coachSearchCriteria, setCoachSearchCriteria] = useState({ voivodeship: '', county: '' });
  const [coachSearchResults, setCoachSearchResults] = useState([]);
  const [isLoadingCoachSearch, setIsLoadingCoachSearch] = useState(false);
  const [coachSearchError, setCoachSearchError] = useState('');
  const [coachSearchPagination, setCoachSearchPagination] = useState({
    currentPage: 0, totalPages: 0, totalElements: 0, pageSize: 10, isFirst: true, isLast: true
  });

  // Pobierz drużyny i słowniki
  useEffect(() => {
    const fetchData = async () => {
      if (isCoach) {
        if (!userId) {
          setError('Nie znaleziono ID użytkownika');
          setIsLoading(false);
          return;
        }
        try {
          const teamsData = await TeamControllerService.getTeamsByUserId(userId);
          setTeams(teamsData || []);
        } catch (err) {
          setError('Nie udało się pobrać danych');
        } finally {
          setIsLoading(false);
        }
      } else if (isSportsClub) {
        if (!sportsClubId) {
          setError('Nie znaleziono ID klubu');
          setIsLoading(false);
          return;
        }
        try {
          const [teamsData, dictionariesData] = await Promise.all([
            TeamControllerService.getTeamsBySportsClubId(sportsClubId),
            DictionaryControllerService.getDictionaries()
          ]);
          setTeams(teamsData || []);
          setDictionaries(dictionariesData || {});
        } catch (err) {
          setError('Nie udało się pobrać danych');
        } finally {
          setIsLoading(false);
        }
      }
    };

    fetchData();
  }, [isCoach, isSportsClub, sportsClubId, userId]);

  // Pobierz słowniki (województwa) dla coach
  useEffect(() => {
    if (isCoach) {
      const fetchTerritories = async () => {
        try {
          const dictionariesData = await DictionaryControllerService.getDictionaries();
          if (dictionariesData?.territories) {
            setTerritories(dictionariesData.territories);
          }
        } catch (err) {
          // error handled silently
        }
      };
      fetchTerritories();
    }
  }, [isCoach]);

  // Pobierz drużyny dla wybranego klubu w wizardzie
  useEffect(() => {
    if (wizardSelectedClubId) {
      setIsLoadingWizardTeams(true);
      TeamControllerService.getTeamsBySportsClubId(wizardSelectedClubId)
        .then(data => setWizardClubTeams(data || []))
        .catch(() => setWizardClubTeams([]))
        .finally(() => setIsLoadingWizardTeams(false));
    } else {
      setWizardClubTeams([]);
    }
  }, [wizardSelectedClubId]);

  // Funkcje formatujące
  const formatSeniorTier = (tier) => {
    const labels = {
      'EKSTRAKLASA': 'Ekstraklasa',
      'FIRST_LEAGUE': 'I Liga',
      'SECOND_LEAGUE': 'II Liga',
      'THIRD_LEAGUE': 'III Liga',
      'FOURTH_LEAGUE': 'IV Liga',
      'FIFTH_LEAGUE': 'V Liga',
      'REGIONAL_CLASS': 'Klasa Okręgowa',
      'CLASS_A': 'Klasa A',
      'CLASS_B': 'Klasa B',
      'CLASS_C': 'Klasa C'
    };
    return labels[tier] || tier;
  };

  const formatTeamSubtype = (type) => {
    const labels = {
      'FIRST_TEAM': 'Pierwsza drużyna',
      'SECOND_TEAM': 'Druga drużyna',
      'THIRD_TEAM': 'Trzecia drużyna'
    };
    return labels[type] || type;
  };

  const formatGender = (gender, isYouth = false) => {
    if (isYouth) {
      return gender === 'MALE' ? 'Chłopcy' : 'Dziewczęta';
    }
    return gender === 'MALE' ? 'Mężczyźni' : 'Kobiety';
  };

  // === SPORTS_CLUB functions ===

  const addSeniorTeam = async () => {
    if (!newSeniorTeam.seniorTeamType) { setError('Podaj rodzaj drużyny'); return; }
    if (!newSeniorTeam.seniorTier) { setError('Podaj poziom rozgrywkowy drużyny'); return; }
    if (!newSeniorTeam.gender) { setError('Podaj płeć'); return; }

    setIsSaving(true);
    setError('');
    try {
      await TeamControllerService.createTeam({
        sportsClubId, teamType: 'SENIOR',
        teamSubtype: newSeniorTeam.seniorTeamType,
        tier: newSeniorTeam.seniorTier,
        gender: newSeniorTeam.gender,
        teamMode: newSeniorTeam.teamMode || 'SEASONAL'
      });
      const updatedTeams = await TeamControllerService.getTeamsBySportsClubId(sportsClubId);
      const oldTeamIds = new Set(teams.map(t => t.teamId));
      const newTeam = (updatedTeams || []).find(t => !oldTeamIds.has(t.teamId));
      if (newTeam) dispatch(addTeam({ id: newTeam.teamId, teamName: newTeam.name, isPreseasonDefined: false }));
      setTeams(updatedTeams || []);
      setNewSeniorTeam({ teamType: 'SENIOR', seniorTier: '', seniorTeamType: '', gender: '', teamMode: 'SEASONAL' });
      setShowSeniorTeamModal(false);
      toast.success('Drużyna seniorska została dodana');
    } catch (err) {
      setError('Nie udało się dodać drużyny: ' + (err.message || 'Nieznany błąd'));
    } finally { setIsSaving(false); }
  };

  const addYouthTeam = async () => {
    if (!newYouthTeam.youthTeamType) { setError('Podaj rodzaj drużyny'); return; }
    if (!newYouthTeam.yearOfBirth) { setError('Podaj rocznik'); return; }
    if (!newYouthTeam.youthTier) { setError('Podaj poziom rozgrywkowy drużyny'); return; }
    if (!newYouthTeam.gender) { setError('Podaj płeć'); return; }

    setIsSaving(true);
    setError('');
    try {
      await TeamControllerService.createTeam({
        sportsClubId, teamType: 'YOUTH',
        teamSubtype: newYouthTeam.youthTeamType,
        tier: newYouthTeam.youthTier,
        gender: newYouthTeam.gender,
        yearOfBirth: newYouthTeam.yearOfBirth,
        teamMode: newYouthTeam.teamMode || 'SEASONAL'
      });
      const updatedTeams = await TeamControllerService.getTeamsBySportsClubId(sportsClubId);
      const oldTeamIds = new Set(teams.map(t => t.teamId));
      const newTeam = (updatedTeams || []).find(t => !oldTeamIds.has(t.teamId));
      if (newTeam) dispatch(addTeam({ id: newTeam.teamId, teamName: newTeam.name, isPreseasonDefined: false }));
      setTeams(updatedTeams || []);
      setNewYouthTeam({ teamType: 'YOUTH', youthTeamType: '', yearOfBirth: '', youthTier: '', gender: '', teamMode: 'SEASONAL' });
      setShowYouthTeamModal(false);
      toast.success('Drużyna juniorska została dodana');
    } catch (err) {
      setError('Nie udało się dodać drużyny: ' + (err.message || 'Nieznany błąd'));
    } finally { setIsSaving(false); }
  };

  const confirmDeleteTeam = (team) => { setTeamToDelete(team); setShowDeleteConfirm(true); };

  const deleteTeam = async () => {
    if (!teamToDelete) return;
    setIsDeleting(true);
    setError('');
    try {
      await TeamControllerService.deleteTeam(teamToDelete.teamId);
      dispatch(removeTeam(teamToDelete.teamId));
      const updatedTeams = await TeamControllerService.getTeamsBySportsClubId(sportsClubId);
      setTeams(updatedTeams || []);
      setShowDeleteConfirm(false);
      setTeamToDelete(null);
      toast.success('Drużyna została usunięta');
    } catch (err) {
      const status = err?.status || err?.response?.status;
      if (status === 409) {
        setError('Nie można usunąć drużyny, do której jest przypisany trener. Najpierw usuń trenera z drużyny.');
      } else {
        setError('Nie udało się usunąć drużyny: ' + (err.message || 'Nieznany błąd'));
      }
      setShowDeleteConfirm(false);
      setTeamToDelete(null);
    } finally { setIsDeleting(false); }
  };

  const openEditTierModal = (team) => { setTeamToEditTier(team); setSelectedTier(team.tier || ''); setShowEditTierModal(true); };

  const updateTierHandler = async () => {
    if (!teamToEditTier || !selectedTier) return;
    setIsUpdatingTier(true);
    setError('');
    try {
      await TeamControllerService.updateTier(teamToEditTier.teamId, selectedTier);
      const updatedTeams = await TeamControllerService.getTeamsBySportsClubId(sportsClubId);
      setTeams(updatedTeams || []);
      setShowEditTierModal(false);
      setTeamToEditTier(null);
      setSelectedTier('');
      toast.success('Poziom rozgrywkowy został zaktualizowany');
    } catch (err) {
      setError('Nie udało się zaktualizować poziomu: ' + (err.message || 'Nieznany błąd'));
    } finally { setIsUpdatingTier(false); }
  };

  // === COACH functions ===

  const confirmUnassignTeam = (team) => { setTeamToUnassign(team); setShowUnassignConfirm(true); };

  const unassignTeam = async () => {
    if (!teamToUnassign) return;
    setIsUnassigning(true);
    setError('');
    try {
      const teamId = teamToUnassign.id || teamToUnassign.teamId;
      await UserManagementControllerService.removeCoachFromTeam(teamId, userId);
      dispatch(removeTeam(teamId));
      const updatedTeams = await TeamControllerService.getTeamsByUserId(userId);
      setTeams(updatedTeams || []);
      setShowUnassignConfirm(false);
      setTeamToUnassign(null);
    } catch (err) {
      setError('Nie udało się usunąć przypisania: ' + (err.message || 'Nieznany błąd'));
    } finally { setIsUnassigning(false); }
  };

  // === COACH: funkcje wizarda dodawania drużyny ===

  const handleCoachSearchChange = (e) => {
    const { name, value } = e.target;
    if (name === 'voivodeship') {
      setCoachSearchCriteria(prev => ({ ...prev, voivodeship: value, county: '' }));
    } else {
      setCoachSearchCriteria(prev => ({ ...prev, [name]: value }));
    }
  };

  const getCountiesForCoachSearch = () => {
    if (!coachSearchCriteria.voivodeship) return [];
    const territory = territories.find(t => t.key === coachSearchCriteria.voivodeship);
    return territory?.subValues || [];
  };

  const performCoachSearch = async (page = 0) => {
    setIsLoadingCoachSearch(true);
    setCoachSearchError('');
    try {
      const criteria = {
        county: coachSearchCriteria.county || undefined,
        voivodeship: coachSearchCriteria.voivodeship || undefined
      };
      const response = await SportsClubControllerService.searchSportsClubs(criteria, page, coachSearchPagination.pageSize);
      setCoachSearchResults(response.content || []);
      setCoachSearchPagination({
        currentPage: response.number ?? 0,
        totalPages: response.totalPages ?? 0,
        totalElements: response.totalElements ?? 0,
        pageSize: response.size ?? 10,
        isFirst: response.first ?? true,
        isLast: response.last ?? true
      });
      if (!response.content || response.content.length === 0) {
        setCoachSearchError('Nie znaleziono klubów spełniających kryteria');
      }
    } catch (err) {
      setCoachSearchError('Wystąpił błąd podczas wyszukiwania');
    } finally {
      setIsLoadingCoachSearch(false);
    }
  };

  const handleResetCoachSearch = () => {
    setCoachSearchCriteria({ voivodeship: '', county: '' });
    setCoachSearchResults([]);
    setCoachSearchError('');
    setCoachSearchPagination({ currentPage: 0, totalPages: 0, totalElements: 0, pageSize: 10, isFirst: true, isLast: true });
  };

  const openCoachAddTeamModal = () => {
    setShowCoachAddTeamModal(true);
    setCoachWizardStep(1);
    setWizardTempSelectedTeamIds([]);
    setWizardSelectedClubId(null);
    setWizardClubTeams([]);
    handleResetCoachSearch();
    setError('');
  };

  const closeCoachAddTeamModal = () => {
    setShowCoachAddTeamModal(false);
    setCoachWizardStep(1);
    setWizardTempSelectedTeamIds([]);
  };

  const handleWizardConfirmTeams = async () => {
    if (wizardTempSelectedTeamIds.length === 0) return;
    setIsAddingPending(true);
    setError('');
    try {
      for (const teamId of wizardTempSelectedTeamIds) {
        await UserTeamControllerService.addPendingUserTeam({ userId, teamId });
      }
      const updatedTeams = await TeamControllerService.getTeamsByUserId(userId);
      setTeams(updatedTeams || []);
      closeCoachAddTeamModal();
      setMobileCoachTab('pending');
    } catch (err) {
      setError('Nie udało się dodać drużyny: ' + (err.message || 'Nieznany błąd'));
    } finally {
      setIsAddingPending(false);
    }
  };

  // Filtruj drużyny według typu
  const getTeamId = (team) => team.teamId || team.id;
  const getTeamName = (team) => team.name || team.teamName || `Drużyna #${getTeamId(team)}`;
  const getTeamType = (team) => team.teamType;
  const approvedTeams = isCoach ? teams.filter(t => !t.isPending) : teams;
  const pendingTeams = isCoach ? teams.filter(t => t.isPending) : [];
  const seniorTeams = approvedTeams.filter(t => getTeamType(t) === 'SENIOR' || getTeamType(t) === 'Seniorzy');
  const youthTeams = approvedTeams.filter(t => getTeamType(t) === 'YOUTH' || getTeamType(t) === 'Juniorzy');

  if (isLoading) {
    return (
      <div className="team-management-container">
        <div className="team-management-card">
          <div className="team-management-header">
            <h1>Zarządzanie drużynami</h1>
          </div>
          <div className="team-management-content">
            <p>Ładowanie...</p>
          </div>
        </div>
      </div>
    );
  }

  // === COACH render ===
  const renderCoachTeamItem = (team) => (
    <div key={getTeamId(team)} className="team-item">
      <div className="team-info">
        <div className="team-info-row">
          <strong>Nazwa:</strong> {getTeamName(team)}
        </div>
        {team.tier && (
          <div className="team-info-row">
            <strong>Poziom rozgrywkowy:</strong> {formatSeniorTier(team.tier)}
          </div>
        )}
        {team.gender && (
          <div className="team-info-row">
            <strong>Płeć:</strong> {formatGender(team.gender, getTeamType(team) === 'YOUTH' || getTeamType(team) === 'Juniorzy')}
          </div>
        )}
      </div>
      <div className="team-actions">
        <button
          type="button"
          onClick={() => confirmUnassignTeam(team)}
          className="btn btn-danger btn-small"
        >
          Usuń przypisanie
        </button>
      </div>
    </div>
  );

  // === SPORTS_CLUB render ===
  const renderSportsClubSeniorItem = (team) => (
    <div key={team.teamId} className="team-item">
      <div className="team-info">
        {team.name && (
          <div className="team-info-row">
            <strong>Nazwa:</strong> {team.name}
          </div>
        )}
        <div className="team-info-row">
          <strong>Poziom rozgrywkowy:</strong> {formatSeniorTier(team.tier)}
        </div>
        {team.gender && (
          <div className="team-info-row">
            <strong>Płeć:</strong> {formatGender(team.gender)}
          </div>
        )}
      </div>
      <div className="team-actions">
        <button type="button" onClick={() => openEditTierModal(team)} className="btn btn-primary btn-small">
          Edytuj poziom
        </button>
        <button type="button" onClick={() => confirmDeleteTeam(team)} className="btn btn-danger btn-small">
          Usuń
        </button>
      </div>
    </div>
  );

  const renderSportsClubYouthItem = (team) => (
    <div key={team.teamId} className="team-item">
      <div className="team-info">
        {team.name && (
          <div className="team-info-row">
            <strong>Nazwa:</strong> {team.name}
          </div>
        )}
        {team.yearOfBirth && (
          <div className="team-info-row">
            <strong>Rocznik:</strong> {dictionaries?.yearOfBirths?.find(y => y.key === team.yearOfBirth)?.displayValue || team.yearOfBirth}
          </div>
        )}
        <div className="team-info-row">
          <strong>Poziom rozgrywkowy:</strong> {dictionaries?.tiers?.find(t => t.key === team.tier && t.teamType === 'YOUTH')?.displayValue || team.tier}
        </div>
        {team.gender && (
          <div className="team-info-row">
            <strong>Płeć:</strong> {formatGender(team.gender, true)}
          </div>
        )}
      </div>
      <div className="team-actions">
        <button type="button" onClick={() => openEditTierModal(team)} className="btn btn-primary btn-small">
          Edytuj poziom
        </button>
        <button type="button" onClick={() => confirmDeleteTeam(team)} className="btn btn-danger btn-small">
          Usuń
        </button>
      </div>
    </div>
  );

  return (
    <div className="team-management-container">
      <div className="team-management-card">
        {!embedded && <h2 className="page-title">Zarządzanie drużynami</h2>}

        {isSportsClub && (
          <div className="add-team-buttons">
            <button type="button" onClick={() => setShowSeniorTeamModal(true)} className="btn btn-primary">
              Dodaj drużynę seniorską
            </button>
          </div>
        )}

        {isCoach && (
          <div className="add-team-buttons">
            <button type="button" onClick={openCoachAddTeamModal} className="btn btn-primary">
              Wyślij prośbę o przypisanie drużyny
            </button>
          </div>
        )}

        {/* Mobile tabs - tylko dla coacha */}
        {isCoach && (
          <div className="tm-mobile-tabs">
            <button
              className={`tm-mobile-tab ${mobileCoachTab === 'approved' ? 'tm-mobile-tab--active' : ''}`}
              onClick={() => setMobileCoachTab('approved')}
            >
              Zatwierdzone
            </button>
            <button
              className={`tm-mobile-tab ${mobileCoachTab === 'pending' ? 'tm-mobile-tab--active' : ''}`}
              onClick={() => setMobileCoachTab('pending')}
            >
              Oczekujące {pendingTeams.length > 0 && `(${pendingTeams.length})`}
            </button>
          </div>
        )}

        <div className="team-management-content">
          {error && <div className="error-message">{error}</div>}

          {/* Desktop: nagłówek "Zatwierdzone" */}
          {isCoach && <h2 className="section-title tm-desktop-only">Zatwierdzone</h2>}

          {/* Sekcje drużyn - na mobile coach widzi je tylko w zakładce "Zatwierdzone" */}
          <div className={`teams-sections-wrapper ${isCoach ? `tm-coach-approved ${mobileCoachTab === 'approved' ? 'tm-mobile-visible' : 'tm-mobile-hidden'}` : ''}`}>
            {/* Sekcja drużyn seniorskich */}
            <div className="team-section team-section-senior">
              <div className="team-section-header">
                <h3>Drużyny seniorskie</h3>
              </div>

              {seniorTeams.length > 0 ? (
                <div className="teams-list">
                  {seniorTeams.map(team => isCoach ? renderCoachTeamItem(team) : renderSportsClubSeniorItem(team))}
                </div>
              ) : (
                <p className="no-teams-message">Brak drużyn seniorskich</p>
              )}
            </div>

          </div>

          {/* Sekcja oczekujących - na mobile coach widzi ją tylko w zakładce "Oczekujące" */}
          {isCoach && (
            <div className={`tm-coach-pending ${mobileCoachTab === 'pending' ? 'tm-mobile-visible' : 'tm-mobile-hidden'}`}>
              <h2 className="section-title tm-desktop-only">Oczekujące</h2>
              {pendingTeams.length > 0 ? (
                <div className="teams-list">
                  {pendingTeams.map(team => (
                    <div key={getTeamId(team)} className="team-item">
                      <div className="team-info">
                        <div className="team-info-row">
                          <strong>Nazwa:</strong> {getTeamName(team)}
                        </div>
                        {team.tier && (
                          <div className="team-info-row">
                            <strong>Poziom rozgrywkowy:</strong> {formatSeniorTier(team.tier)}
                          </div>
                        )}
                      </div>
                      <div className="team-actions">
                        <span className="pending-badge">Oczekuje na zatwierdzenie</span>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="no-teams-message">Brak oczekujących drużyn</p>
              )}
            </div>
          )}
        </div>
      </div>

      {/* === Modale SPORTS_CLUB === */}

      {/* Modal dodawania drużyny seniorskiej */}
      {showSeniorTeamModal && (
        <div className="modal-overlay" onClick={() => {
          setShowSeniorTeamModal(false);
          setNewSeniorTeam({ teamType: 'SENIOR', seniorTier: '', seniorTeamType: '', gender: '', teamMode: 'SEASONAL' });
          setError('');
        }}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h2>Dodaj drużynę seniorską</h2>
            {error && <div className="error-message">{error}</div>}
            <div className="form-group">
              <label htmlFor="seniorTeamTier">Poziom rozgrywkowy</label>
              <select id="seniorTeamTier" value={newSeniorTeam.seniorTier}
                onChange={(e) => setNewSeniorTeam(prev => ({ ...prev, seniorTier: e.target.value }))} className="form-select">
                <option value="">-- Wybierz --</option>
                <option value="EKSTRAKLASA">Ekstraklasa</option>
                <option value="FIRST_LEAGUE">I Liga</option>
                <option value="SECOND_LEAGUE">II Liga</option>
                <option value="THIRD_LEAGUE">III Liga</option>
                <option value="FOURTH_LEAGUE">IV Liga</option>
                <option value="FIFTH_LEAGUE">V Liga</option>
                <option value="REGIONAL_CLASS">Klasa Okręgowa</option>
                <option value="CLASS_A">Klasa A</option>
                <option value="CLASS_B">Klasa B</option>
                <option value="CLASS_C">Klasa C</option>
              </select>
            </div>
            <div className="form-group">
              <label htmlFor="seniorTeamType">Rodzaj drużyny</label>
              <select id="seniorTeamType" value={newSeniorTeam.seniorTeamType}
                onChange={(e) => setNewSeniorTeam(prev => ({ ...prev, seniorTeamType: e.target.value }))} className="form-select">
                <option value="">-- Wybierz --</option>
                <option value="FIRST_TEAM">Pierwsza drużyna</option>
                <option value="SECOND_TEAM">Druga drużyna</option>
                <option value="THIRD_TEAM">Trzecia drużyna</option>
              </select>
            </div>
            <div className="form-group">
              <label htmlFor="seniorTeamGender">Płeć</label>
              <select id="seniorTeamGender" value={newSeniorTeam.gender}
                onChange={(e) => setNewSeniorTeam(prev => ({ ...prev, gender: e.target.value }))} className="form-select">
                <option value="">-- Wybierz --</option>
                <option value="MALE">Mężczyźni</option>
                <option value="FEMALE">Kobiety</option>
              </select>
            </div>
            <div className="modal-actions">
              <button type="button" onClick={() => {
                setShowSeniorTeamModal(false);
                setNewSeniorTeam({ teamType: 'SENIOR', seniorTier: '', seniorTeamType: '', gender: '', teamMode: 'SEASONAL' });
                setError('');
              }} className="btn btn-secondary" disabled={isSaving}>Anuluj</button>
              <button type="button" onClick={addSeniorTeam} className="btn btn-primary"
                disabled={!newSeniorTeam.seniorTeamType || !newSeniorTeam.seniorTier || !newSeniorTeam.gender || isSaving}>
                {isSaving ? 'Dodawanie...' : 'Dodaj'}
              </button>
            </div>
          </div>
        </div>
      )}


      {/* Modal edycji poziomu */}
      {showEditTierModal && (
        <div className="modal-overlay" onClick={() => {
          setShowEditTierModal(false); setTeamToEditTier(null); setSelectedTier(''); setError('');
        }}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h2>Edytuj poziom rozgrywkowy</h2>
            {error && <div className="error-message">{error}</div>}
            <p>Drużyna: <strong>{teamToEditTier?.name || '—'}</strong></p>
            <div className="form-group">
              <label htmlFor="editTierSelect">Poziom rozgrywkowy</label>
              <select id="editTierSelect" value={selectedTier}
                onChange={(e) => setSelectedTier(e.target.value)} className="form-select">
                <option value="">-- Wybierz --</option>
                {(teamToEditTier?.teamType === 'SENIOR' || teamToEditTier?.teamType === 'Seniorzy') ? (
                  <>
                    <option value="EKSTRAKLASA">Ekstraklasa</option>
                    <option value="FIRST_LEAGUE">I Liga</option>
                    <option value="SECOND_LEAGUE">II Liga</option>
                    <option value="THIRD_LEAGUE">III Liga</option>
                    <option value="FOURTH_LEAGUE">IV Liga</option>
                    <option value="REGIONAL_CLASS">Klasa Okręgowa</option>
                    <option value="CLASS_A">Klasa A</option>
                    <option value="CLASS_B">Klasa B</option>
                    <option value="CLASS_C">Klasa C</option>
                  </>
                ) : (
                  dictionaries?.tiers?.filter(tier => tier.teamType === 'YOUTH').map((tier) => (
                    <option key={tier.key} value={tier.key}>{tier.displayValue}</option>
                  ))
                )}
              </select>
            </div>
            <div className="modal-actions">
              <button type="button" onClick={() => {
                setShowEditTierModal(false); setTeamToEditTier(null); setSelectedTier(''); setError('');
              }} className="btn btn-secondary" disabled={isUpdatingTier}>Anuluj</button>
              <button type="button" onClick={updateTierHandler} className="btn btn-primary"
                disabled={!selectedTier || isUpdatingTier}>
                {isUpdatingTier ? 'Zapisywanie...' : 'Zapisz'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal potwierdzenia usunięcia drużyny (SPORTS_CLUB) */}
      {showDeleteConfirm && (
        <div className="modal-overlay" onClick={() => { setShowDeleteConfirm(false); setTeamToDelete(null); }}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h2>Potwierdź usunięcie</h2>
            <p className="delete-confirm-message">
              Czy na pewno chcesz usunąć drużynę <strong>{teamToDelete?.name || 'tę drużynę'}</strong>?
            </p>
            <p className="delete-warning">Ta operacja jest nieodwracalna.</p>
            <div className="modal-actions">
              <button type="button" onClick={() => { setShowDeleteConfirm(false); setTeamToDelete(null); }}
                className="btn btn-secondary" disabled={isDeleting}>Anuluj</button>
              <button type="button" onClick={deleteTeam} className="btn btn-danger" disabled={isDeleting}>
                {isDeleting ? 'Usuwanie...' : 'Usuń'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal potwierdzenia usunięcia przypisania (COACH) */}
      {showUnassignConfirm && (
        <div className="modal-overlay" onClick={() => { setShowUnassignConfirm(false); setTeamToUnassign(null); }}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h2>Usuń przypisanie do drużyny</h2>
            <p className="delete-confirm-message">
              Czy na pewno chcesz usunąć swoje przypisanie do drużyny <strong>{getTeamName(teamToUnassign)}</strong>?
            </p>
            <p className="delete-warning">
              Stracisz dostęp do tej drużyny. Aby odzyskać przypisanie, klub będzie musiał ponownie Cię zaakceptować.
            </p>
            <div className="modal-actions">
              <button type="button" onClick={() => { setShowUnassignConfirm(false); setTeamToUnassign(null); }}
                className="btn btn-secondary" disabled={isUnassigning}>Nie</button>
              <button type="button" onClick={unassignTeam} className="btn btn-danger" disabled={isUnassigning}>
                {isUnassigning ? 'Usuwanie...' : 'Tak, usuń'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal wizarda dodawania drużyny (COACH) */}
      {showCoachAddTeamModal && (
        <div className="modal-overlay" onClick={closeCoachAddTeamModal}>
          <div className="modal-content coach-add-team-modal" onClick={(e) => e.stopPropagation()}>
            {/* Wizard step indicator */}
            <div className="wizard-steps">
              <div className={`wizard-step-circle ${coachWizardStep >= 1 ? 'active' : ''}`}>
                {coachWizardStep > 1 ? '\u2713' : '1'}
              </div>
              <div className={`wizard-step-line ${coachWizardStep > 1 ? 'active' : ''}`} />
              <div className={`wizard-step-circle ${coachWizardStep === 2 ? 'active' : ''}`}>
                2
              </div>
            </div>

            {coachWizardStep === 1 && (
              <>
                <h2 className="wizard-title">Wyszukaj klub</h2>
                <p className="wizard-subtitle">
                  Użyj filtrów, aby znaleźć klub
                </p>

                <div className="wizard-filters-row">
                  <div className="form-group wizard-filter-field">
                    <label htmlFor="wizard-voivodeship">Województwo</label>
                    <select
                      id="wizard-voivodeship"
                      name="voivodeship"
                      value={coachSearchCriteria.voivodeship}
                      onChange={handleCoachSearchChange}
                      className="form-select"
                    >
                      <option value="">-- Wszystkie --</option>
                      {territories.map((territory) => (
                        <option key={territory.key} value={territory.key}>
                          {territory.displayValue}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="form-group wizard-filter-field">
                    <label htmlFor="wizard-county">Powiat</label>
                    <select
                      id="wizard-county"
                      name="county"
                      value={coachSearchCriteria.county}
                      onChange={handleCoachSearchChange}
                      className="form-select"
                      disabled={!coachSearchCriteria.voivodeship}
                    >
                      <option value="">-- Wszystkie --</option>
                      {getCountiesForCoachSearch().map((county) => (
                        <option key={county.key} value={county.key}>
                          {county.displayValue}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                <div className="wizard-search-buttons">
                  <button type="button" onClick={handleResetCoachSearch} className="btn btn-secondary">
                    Wyczyść
                  </button>
                  <button type="button" onClick={(e) => { e.preventDefault(); performCoachSearch(); }} className="btn btn-primary" disabled={isLoadingCoachSearch}>
                    {isLoadingCoachSearch ? 'Wyszukiwanie...' : 'Szukaj'}
                  </button>
                </div>

                {coachSearchError && <div className="error-message">{coachSearchError}</div>}

                {coachSearchResults.length > 0 && (
                  <div className="wizard-results-section">
                    <h4 className="wizard-results-title">Wyniki ({coachSearchResults.length} z {coachSearchPagination.totalElements})</h4>
                    <div className="results-grid">
                      {coachSearchResults.map((club, index) => (
                        <div
                          key={index}
                          className={`result-card ${wizardSelectedClubId === club.id ? 'selected' : ''}`}
                        >
                          {club.logoUrl && (
                            <img src={club.logoUrl} alt={`Logo ${club.name}`} className="wizard-club-logo" />
                          )}
                          <h5>{club.name}</h5>
                          <div className="team-info">
                            <p><strong>Woj.:</strong> {club.voivodeship}</p>
                            <p><strong>Pow.:</strong> {club.county}</p>
                          </div>
                          <button type="button" className="btn btn-primary btn-select" onClick={() => { setWizardSelectedClubId(club.id); setCoachSearchError(''); }}>
                            {wizardSelectedClubId === club.id ? 'Wybrano' : 'Wybierz'}
                          </button>
                        </div>
                      ))}
                    </div>

                    {coachSearchPagination.totalPages > 1 && (
                      <div className="wizard-pagination">
                        <button type="button" onClick={() => performCoachSearch(coachSearchPagination.currentPage - 1)} disabled={coachSearchPagination.isFirst || isLoadingCoachSearch} className="btn btn-secondary">
                          Poprzednia
                        </button>
                        <span className="wizard-pagination-info">
                          {coachSearchPagination.currentPage + 1} / {coachSearchPagination.totalPages}
                        </span>
                        <button type="button" onClick={() => performCoachSearch(coachSearchPagination.currentPage + 1)} disabled={coachSearchPagination.isLast || isLoadingCoachSearch} className="btn btn-secondary">
                          Następna
                        </button>
                      </div>
                    )}
                  </div>
                )}

                <div className="modal-actions">
                  <button type="button" onClick={closeCoachAddTeamModal} className="btn btn-secondary">Anuluj</button>
                  <button
                    type="button"
                    onClick={() => { setCoachWizardStep(2); setWizardTempSelectedTeamIds([]); }}
                    className="btn btn-primary"
                    disabled={!wizardSelectedClubId}
                  >
                    Dalej
                  </button>
                </div>
              </>
            )}

            {coachWizardStep === 2 && (() => {
              const wizSeniorTeams = wizardClubTeams.filter(team => team.teamType === 'SENIOR' || team.teamType === 'Seniorzy');
              const wizYouthTeams = wizardClubTeams.filter(team => team.teamType === 'YOUTH' || team.teamType === 'Juniorzy');
              const alreadyAdded = teams.map(t => getTeamId(t));

              const handleWizardTeamToggle = (teamId) => {
                setWizardTempSelectedTeamIds(prev =>
                  prev.includes(teamId) ? prev.filter(id => id !== teamId) : [...prev, teamId]
                );
              };

              const renderWizardTeamCheckbox = (team) => {
                const isAlreadyAdded = alreadyAdded.includes(team.teamId);
                const isSelected = wizardTempSelectedTeamIds.includes(team.teamId);
                const teamName = team.name || 'Drużyna';
                const teamLabel = team.tier ? `${teamName} (${formatSeniorTier(team.tier)})` : teamName;

                return (
                  <label
                    key={team.teamId}
                    className={`wizard-team-checkbox ${isAlreadyAdded ? 'disabled' : ''} ${isSelected ? 'selected' : ''}`}
                  >
                    <input
                      type="checkbox"
                      checked={isSelected || isAlreadyAdded}
                      onChange={() => !isAlreadyAdded && handleWizardTeamToggle(team.teamId)}
                      disabled={isAlreadyAdded}
                      className="wizard-team-input"
                    />
                    <span className="wizard-team-label">
                      {teamLabel}
                      {isAlreadyAdded && <span className="wizard-team-added">(dodano)</span>}
                    </span>
                  </label>
                );
              };

              return (
                <>
                  <h2 className="wizard-title">Wybierz drużyny</h2>
                  <p className="wizard-subtitle">
                    Wybierz drużyny, do których chcesz dołączyć
                  </p>

                  {isLoadingWizardTeams ? (
                    <p>Ładowanie drużyn...</p>
                  ) : wizardClubTeams.length === 0 ? (
                    <div className="error-message">Wybrany klub nie ma zdefiniowanych drużyn</div>
                  ) : (
                    <div className="wizard-teams-columns">
                      <div className="wizard-teams-column">
                        <h4 className="wizard-teams-column-title wizard-teams-senior">Seniorzy</h4>
                        <div className="wizard-teams-list">
                          {wizSeniorTeams.length === 0
                            ? <p className="wizard-no-teams">Brak drużyn seniorskich</p>
                            : wizSeniorTeams.map(renderWizardTeamCheckbox)}
                        </div>
                      </div>
                    </div>
                  )}

                  <div className="modal-actions">
                    <button type="button" onClick={() => setCoachWizardStep(1)} className="btn btn-secondary" disabled={isAddingPending}>Wstecz</button>
                    <button
                      type="button"
                      onClick={handleWizardConfirmTeams}
                      className="btn btn-primary"
                      disabled={wizardTempSelectedTeamIds.length === 0 || isAddingPending}
                    >
                      {isAddingPending ? 'Dodawanie...' : `Dodaj wybrane (${wizardTempSelectedTeamIds.length})`}
                    </button>
                  </div>
                </>
              );
            })()}
          </div>
        </div>
      )}
    </div>
  );
}

export default TeamManagement;
